// assets/decklist-search.js

class DecklistSearch extends Global.Core {
    constructor() {
        super();
        this.elements = {
            'input': '[data-element="decklist-input"]',
            'searchButton': '[data-element="search-button"]',
            'resultsContainer': '[data-element="results-container"]',
            'selectAllCheckbox': '[data-element="select-all"]',
            'addToCartButton': '[data-element="add-to-cart-button"]',
            'collectionSelect': '[data-element="collection-select"]',
            'totalPriceDisplay': '[data-element="total-price-display"]',
        };
        this.activePopover = null;
        this.activeResultRow = null;
    }

    render() {

        this.$('searchButton', { click: this._onSearch.bind(this) });
        this.$('addToCartButton', { click: this._onAddToCart.bind(this) });
        this.$('selectAllCheckbox', { change: this._onSelectAllChange.bind(this) });

        this.$('resultsContainer').addEventListener('click', (e) => {
            this._onResultsClick(e);
            this._onQuantityButtonClick(e);
        });
        
        this.addEventListener('change', (e) => {
            if (e.target.matches('[data-element="product-checkbox"]')) {
                this._updateTotalPrice();
            }
        });
        
        this.closest('modal-container').addEventListener('modal-close', () => this._closeVersionsPopover());
    }

    _onQuantityButtonClick(e) {

        const minusBtn = e.target.closest('[data-action="decklist-minus"]');
        const plusBtn = e.target.closest('[data-action="decklist-plus"]');

        if (!minusBtn && !plusBtn) return;

        e.preventDefault();
        e.stopPropagation();

        const resultRow = (minusBtn || plusBtn).closest('.decklist-result');
        const quantityEl = resultRow.querySelector('.decklist-qty-value');
        let currentQuantity = parseInt(quantityEl.textContent, 10);

        if (minusBtn && currentQuantity > 1) {
            currentQuantity--;
        } else if (plusBtn) {
            currentQuantity++;
        }
        
        quantityEl.textContent = currentQuantity;
        resultRow.dataset.quantity = currentQuantity;
        
        this._updateTotalPrice();
    }

    _parseInput(text) {
        const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
        return lines.map(line => {
            const match = line.match(/^(\d+)\s+(.*)$/i);
            if (match) {
                return { 
                    quantity: parseInt(match[1], 10), 
                    title: match[2].trim(), 
                    line: line 
                };
            }
            return null;
        }).filter(Boolean);
    }

    async _onSearch(e) {
        e.preventDefault();
        const decklistText = this.$('input').value.trim();
        const collectionHandle = this.$('collectionSelect').value;
    
        const parsedList = this._parseInput(decklistText);
        if (decklistText.length > 0 && parsedList.length === 0) {
            this.pub(Global.Events.TOAST_NOTIFICATION, { type: 'error', msg: 'Invalid format. Each line must start with a quantity.' });
            return;
        }
    
        if (parsedList.length === 0) return;
    
        Global.Utils.$loading(this.$('searchButton'), true);
        this.$('resultsContainer').innerHTML = `<div class="spinner"><div></div><div></div><div></div></div>`;
    
        const results = await Promise.all(parsedList.map(item => this._findProducts(item, collectionHandle)));
        
        // Group results by status to ensure correct ordering
        const found = results.filter(r => r.status === 'found');
        const notFound = results.filter(r => r.status === 'not_found');
        const outOfStock = results.filter(r => r.status === 'out_of_stock');
        const errors = results.filter(r => r.status === 'error');
    
        const sortedResults = [...notFound, ...outOfStock, ...errors, ...found];
    
        this._renderResults(sortedResults);
        Global.Utils.$loading(this.$('searchButton'), false);
    }

    async _findProducts(item, collectionHandle) {
        try {
            const query = item.title;
            const searchUrl = `/search?view=json&type=product&q=${encodeURIComponent(query)}&options[prefix]=last&collections=${collectionHandle}`;
            
            const response = await fetch(searchUrl);
            if (!response.ok) throw new Error(`Initial search failed: ${response.statusText}`);
            const searchData = await response.json();
    
            if (!searchData.results || searchData.results.length === 0) {
                return { ...item, matches: [], status: 'not_found' };
            }
    
            const productPromises = searchData.results.map(r => {
                const handle = r.url.split('?')[0].split('/').pop();
                if (!handle) return Promise.resolve(null);
                return fetch(`/products/${handle}.js`).then(res => res.ok ? res.json() : null);
            });
    
            const fullProducts = (await Promise.all(productPromises)).filter(Boolean);
    
            if (fullProducts.length === 0) {
                return { ...item, matches: [], status: 'not_found' };
            }
            
            const allMatches = fullProducts.map(p => {
                return p.variants.map(variant => ({
                    ...p,
                    variantId: variant.id,
                    price: variant.price,
                    inventory: variant.inventory_quantity,
                    available: variant.available,
                    image: variant.featured_image ? variant.featured_image.src : p.featured_image,
                    title: p.title,
                    variantTitle: variant.title
                }));
            }).flat();
    
            const availableMatches = allMatches.filter(m => m.available);
            
            if (availableMatches.length > 0) {
                const firstAvailable = availableMatches[0];
                const otherMatches = allMatches.filter(m => m.variantId !== firstAvailable.variantId);
                const orderedMatches = [firstAvailable, ...otherMatches];
    
                return { ...item, matches: orderedMatches, status: 'found' };
            } else {
                return { ...item, matches: [], status: 'out_of_stock', title: fullProducts[0].title };
            }
    
        } catch (error) {
            console.error(`[DecklistSearch] Search process failed for "${item.title}":`, error);
            return { ...item, matches: [], status: 'error' };
        }
    }

    _renderResults(results) {
        const container = this.$('resultsContainer');
        container.innerHTML = results.map(item => {
            switch (item.status) {
                case 'found':
                    const bestMatch = item.matches[0];
                    const matchesJson = JSON.stringify(item.matches).replace(/'/g, "&#39;");
                    const priceFormatted = Shopify.formatMoney(bestMatch.price, window.Shopify.money_format);
    
                    let quantityToDisplay = item.quantity;
                    let warning = '';
                    
                    if (bestMatch.inventory > 0 && bestMatch.inventory < item.quantity) {
                        quantityToDisplay = bestMatch.inventory;
                        warning = `<span class="decklist-warning decklist-warning--info">Only ${bestMatch.inventory} available</span>`;
                    } else if (bestMatch.inventory <= 0) {
                        return `<div class="decklist-result decklist-result-out-of-stock"><span>${bestMatch.title} is out of stock.</span></div>`;
                    }
    
                    const versionsButton = item.matches.length > 1 ? `<button type="button" class="decklist-versions-btn" data-requested-quantity="${item.quantity}">Versions (${item.matches.length})</button>` : '';
    
                    return `
                        <div class="decklist-result" data-variant-id="${bestMatch.variantId}" data-quantity="${quantityToDisplay}" data-matches='${matchesJson}' data-price="${bestMatch.price}">
                            <label class="decklist-result-label">
                                <input type="checkbox" data-element="product-checkbox" checked>
                                <img src="${bestMatch.image}" alt="${bestMatch.title}" loading="lazy">
                                <div class="decklist-title-wrapper">
                                    ${warning}
                                    <span class="decklist-title" data-element="title">${bestMatch.title}</span>
                                </div>
                            </label>
                            <div class="decklist-result-details">
                                <div class="decklist-qty-selector">
                                    <button type="button" class="decklist-qty-btn" data-action="decklist-minus">-</button>
                                    <span class="decklist-qty-value">${quantityToDisplay}</span>
                                    <button type="button" class="decklist-qty-btn" data-action="decklist-plus">+</button>
                                </div>
                                <span class="decklist-price" data-element="price">${priceFormatted}</span>
                            </div>
                            <div class="decklist-result-actions">
                                ${versionsButton}
                            </div>
                        </div>
                    `;
    
                case 'out_of_stock':
                    return `<div class="decklist-result decklist-result-out-of-stock"><span>${item.title} is out of stock.</span></div>`;
                
                case 'not_found':
                default:
                    return `<div class="decklist-result decklist-result-not-found"><span>Could not find: ${item.title}</span></div>`;
            }
        }).join('');
        this._updateTotalPrice();
    }

    _onSelectAllChange(e) {
        const isChecked = e.target.checked;
        this.querySelectorAll('[data-element="product-checkbox"]').forEach(checkbox => {
            checkbox.checked = isChecked;
        });
        this._updateTotalPrice();
    }

    _updateTotalPrice() {
        const selectedResults = Array.from(this.querySelectorAll('.decklist-result[data-variant-id]'))
            .filter(el => el.querySelector('[data-element="product-checkbox"]:checked'));

        const total = selectedResults.reduce((sum, el) => {
            const quantity = parseInt(el.dataset.quantity, 10);
            const price = parseInt(el.dataset.price, 10);
            if (isNaN(quantity) || isNaN(price)) {
                console.warn('[DecklistSearch] Found a result item with invalid quantity or price:', el);
                return sum;
            }
            return sum + (price * quantity);
        }, 0);

        this.$('totalPriceDisplay').innerHTML = `Total: ${Shopify.formatMoney(total, window.Shopify.money_format)}`;
    }

    async _onAddToCart(e) {
        e.preventDefault();

        const itemsToAdd = Array.from(this.querySelectorAll('.decklist-result[data-variant-id]'))
            .filter(el => el.querySelector('[data-element="product-checkbox"]:checked'))
            .map(el => ({
                id: parseInt(el.dataset.variantId, 10),
                quantity: parseInt(el.dataset.quantity, 10)
            }));

        if (itemsToAdd.length === 0) {
            this.pub(Global.Events.TOAST_NOTIFICATION, { type: 'warning', msg: 'No items selected.' });
            return;
        }

        Global.Utils.$loading(this.$('addToCartButton'), true);

        this.pub(Global.Events.CART_ADD, { items: itemsToAdd });
        
        setTimeout(() => {
            this.pub(Global.Events.TOAST_NOTIFICATION, { type: 'success', msg: `${itemsToAdd.length} product types added to cart.` });
            this.closest('modal-container').dispatchEvent(new CustomEvent('modal-close', { bubbles: true }));
            Global.Utils.$loading(this.$('addToCartButton'), false);
        }, 1000);
    }
    
    _onResultsClick(e) {
        const versionsBtn = e.target.closest('.decklist-versions-btn');
        if (versionsBtn) {
            e.stopPropagation();
            this.activeResultRow = versionsBtn.closest('.decklist-result');
            const matches = JSON.parse(this.activeResultRow.dataset.matches);
            const requestedQuantity = parseInt(versionsBtn.dataset.requestedQuantity, 10);
            this._showVersionsPopover(matches, requestedQuantity);
        }
    }
    
    _showVersionsPopover(matches, requestedQuantity) {
        this._closeVersionsPopover();
        const popover = document.createElement('dialog');
        popover.className = 'decklist-versions-popover';
        popover.addEventListener('click', (e) => {
            if (e.target === popover) this._closeVersionsPopover();
        });

        popover.innerHTML = `
            <div class="decklist-versions-popover-content">
                <h4>Select a version</h4>
                <ul class="decklist-versions-list">
                    ${matches.map(match => {
                        let itemClass = 'decklist-versions-item';
                        let warningHTML = '';
                        let dataAttributes = `data-variant-id="${match.variantId}"`;

                        if (!match.available) {
                            itemClass += ' decklist-versions-item--disabled';
                            warningHTML = `<small class="decklist-version-stock-status">Out of stock</small>`;
                        } else if (match.inventory < requestedQuantity) {
                            warningHTML = `<small class="decklist-version-stock-warning">Quantity: ${match.inventory}</small>`;
                        }

                        return `
                            <li class="${itemClass}" ${dataAttributes}>
                                <img src="${match.image}" alt="${match.title}" width="50" height="50">
                                <div class="decklist-version-info">
                                    <span>${match.title} (${match.variantTitle})</span>
                                    ${warningHTML}
                                    <small>${Shopify.formatMoney(match.price, window.Shopify.money_format)}</small>
                                </div>
                            </li>
                        `;
                    }).join('')}
                </ul>
            </div>
        `;

        popover.querySelector('.decklist-versions-list').addEventListener('click', (e) => {
            const selectedItem = e.target.closest('.decklist-versions-item');
            if (selectedItem && !selectedItem.classList.contains('decklist-versions-item--disabled')) {
                this._handleVersionSelect(selectedItem, matches, requestedQuantity);
            }
        });
        document.body.appendChild(popover);
        popover.showModal();
        this.activePopover = popover;
    }

    _handleVersionSelect(selectedItem, allMatches, requestedQuantity) {
        const newVariantId = parseInt(selectedItem.dataset.variantId, 10);
        const selectedMatch = allMatches.find(m => m.variantId === newVariantId);
    
        if (selectedMatch && this.activeResultRow) {
            const titleWrapper = this.activeResultRow.querySelector('.decklist-title-wrapper');
            let warningEl = titleWrapper.querySelector('.decklist-warning--info');
    
            if (selectedMatch.inventory < requestedQuantity) {
                this.activeResultRow.dataset.quantity = selectedMatch.inventory;
                this.activeResultRow.querySelector('.decklist-qty-value').textContent = selectedMatch.inventory;
                
                if (!warningEl) {
                    warningEl = document.createElement('span');
                    warningEl.className = 'decklist-warning decklist-warning--info';
                    titleWrapper.prepend(warningEl);
                }
                warningEl.textContent = `Only ${selectedMatch.inventory} available`;
            } else {
                this.activeResultRow.dataset.quantity = requestedQuantity;
                this.activeResultRow.querySelector('.decklist-qty-value').textContent = requestedQuantity;
                if (warningEl) warningEl.remove();
            }
    
            this.activeResultRow.dataset.variantId = selectedMatch.variantId;
            this.activeResultRow.dataset.price = selectedMatch.price;
            this.activeResultRow.querySelector('[data-element="title"]').textContent = selectedMatch.title;
            this.activeResultRow.querySelector('[data-element="price"]').textContent = Shopify.formatMoney(selectedMatch.price, window.Shopify.money_format);
            this.activeResultRow.querySelector('img').src = selectedMatch.image;
            
            const reorderedMatches = [selectedMatch, ...allMatches.filter(m => m.variantId !== newVariantId)];
            this.activeResultRow.dataset.matches = JSON.stringify(reorderedMatches).replace(/'/g, "&#39;");
        }
        this._updateTotalPrice();
        this._closeVersionsPopover();
    }
    
    _closeVersionsPopover() {
        if (this.activePopover) {
            this.activePopover.close();
            this.activePopover.remove();
            this.activePopover = null;
            this.activeResultRow = null;
        }
    }
}

customElements.define('decklist-search', DecklistSearch);